import React, { FC, ChangeEvent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { fet } from '@coinflare/ui/types/assets/images/crypto-icons';

import defaultImage from 'src/assets/images/default-icon.png';
import { DEPTH, FavoriteCoinType, TokenLabel } from 'src/common/constants';
import {
    abbreviateNumber,
    currencyFormatter,
    hasCommonValue,
    isEmptyObj,
    isValidRegex,
} from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { Drawer } from 'src/components/core/drawer';
import {
    Cancel,
    CrossCircle,
    Favorites,
    Hot,
    MoreLess,
    New,
    NoFavorites,
    NoRecord,
    Search,
    SortDown,
    SortUp,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { Tab, Tabs } from 'src/components/core/tabs';
import CategorySelector from 'src/components/features/common/components/CategorySelector';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import {
    createFavorite,
    cancelFavorite,
    fetchUserFeaturesConfig,
} from 'src/setup/redux/slices/userSlice';
import { TradeSocketResponseData } from 'src/types';
import { FutureSymbol, TokenObj } from 'src/types/configV2';

import { Holding } from './holding';
import {
    DrawerContainer,
    DrawerHeader,
    DrawerSearchContainer,
    DrawerCoinListContainer,
    NoRecordContainer,
    DrawerCoinListHeader,
    DrawerCoinListItem,
} from './styles';
interface DrawerProps {
    drawerToggler: () => void;
    isDrawerOpen: boolean;
}
export interface TradeSocketFutureData extends TradeSocketResponseData, FutureSymbol {
    [key: string]: any;
}
export interface TradeSocketFutureDataObj {
    [key: string]: TradeSocketFutureData;
}

const FILTERS = {
    PRICE: 'PRICE' as keyof FILTER_TYPES,
    CHANGE: 'CHANGE' as keyof FILTER_TYPES,
    VOLUME: 'VOLUME' as keyof FILTER_TYPES,
    TRADING_PAIR: 'TRADING_PAIR' as keyof FILTER_TYPES,
};
const FILTER_DIRECTION = {
    ASC: 'ASC',
    DESC: 'DESC',
};
type FilterKey = 'TRADING_PAIR' | 'PRICE' | 'CHANGE' | 'VOLUME';
type FilterValue = {
    direction: 'ASC' | 'DESC';
    key: string;
};

type FILTER_TYPES = {
    // eslint-disable-next-line no-unused-vars
    [key in FilterKey]?: FilterValue;
};
const CoinDrawer: FC<DrawerProps> = ({ drawerToggler, isDrawerOpen }) => {
    const { t } = useTranslation();
    const { isMobile, colors } = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState(2);
    const [activeFilter, setActiveFilter] = useState({
        [FILTERS.VOLUME]: { direction: 'DESC', key: 'qv' },
    } as FILTER_TYPES);
    const { futuresUnderlying, futuresSymbol, token } = useAppSelector(
        (state) => state.global.configV2,
    );
    const slowBroker = useAppSelector((state) => state.socket.slowBroker);
    const { favorites } = useAppSelector((state) => state.user);
    const fetchUserFeaturesConfigLoading = useAppSelector(
        (state) => state.user.loading.fetchUserFeaturesConfig,
    );

    const [categoryFilter, setCategoryFilter] = useState('');

    const { isAuthenticated } = useAuth();

    const filteredSymbols = useMemo(() => {
        let symbolNeedsToUpdate = {} as TradeSocketFutureDataObj;

        if (!isDrawerOpen || fetchUserFeaturesConfigLoading || activeTab === 1)
            return symbolNeedsToUpdate;

        const currentFilter = Object.keys(activeFilter);
        let isValidSearch: RegExpMatchArray | null = null;
        if (isValidRegex(searchText)) {
            isValidSearch = searchText.match(new RegExp('^[a-zA-Z0-9-]*$'));
        }
        Object.values(futuresSymbol)
            .filter(
                (symbol) =>
                    categoryFilter === '' ||
                    (symbol.label &&
                        'labelValue' in symbol.label &&
                        symbol.label.labelValue === categoryFilter),
            )
            .map((symbol) => {
                let matchArr =
                    isValidSearch !== null
                        ? symbol.symbolName.match(new RegExp(searchText, 'ig'))
                        : null;
                if (matchArr !== null && matchArr.length > 0 && isValidSearch !== null) {
                    symbolNeedsToUpdate[symbol.symbolId] = {
                        ...symbol,
                        ...slowBroker[symbol.symbolId],
                    };
                }
            });
        if (activeTab === 0) {
            let favObj: TradeSocketFutureDataObj = {};
            Object.keys(symbolNeedsToUpdate).map((key) => {
                if (favorites.includes(key)) {
                    favObj[key] = symbolNeedsToUpdate[key];
                }
            });
            symbolNeedsToUpdate = favObj;
        }
        if (activeTab !== 1 && currentFilter.length > 0) {
            let typedKey = currentFilter[0] as keyof FILTER_TYPES;
            let key = activeFilter[typedKey]?.key || '';
            let sortDirection = activeFilter[typedKey]?.direction || '';
            if (typedKey === FILTERS.TRADING_PAIR) {
                symbolNeedsToUpdate = Object.entries(symbolNeedsToUpdate)
                    .sort((a, b) =>
                        sortDirection === FILTER_DIRECTION.ASC
                            ? a[1][key].localeCompare(b[1][key])
                            : b[1][key].localeCompare(a[1][key]),
                    )
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
            } else {
                symbolNeedsToUpdate = Object.entries(symbolNeedsToUpdate)
                    .sort((a, b) => {
                        return sortDirection === FILTER_DIRECTION.ASC
                            ? a[1][key] - b[1][key]
                            : b[1][key] - a[1][key];
                    })
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
            }
        }
        return symbolNeedsToUpdate;
    }, [
        activeFilter,
        activeTab,
        favorites,
        futuresSymbol,
        isDrawerOpen,
        searchText,
        slowBroker,
        categoryFilter,
        fetchUserFeaturesConfigLoading,
    ]);

    useEffect(() => {
        setActiveTab(2);
    }, [isDrawerOpen]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const clearInput = () => {
        setSearchText('');
    };

    const setFilter = (filterKey: string, key: string) => {
        let direction = FILTER_DIRECTION.ASC;
        const currentDirection = activeFilter[filterKey as FilterKey]?.direction;
        if (currentDirection === FILTER_DIRECTION.DESC) {
            setActiveFilter({});
            return;
        }
        if (currentDirection === FILTER_DIRECTION.ASC) {
            direction = FILTER_DIRECTION.DESC;
        } else {
            direction = FILTER_DIRECTION.ASC;
        }
        setActiveFilter({
            [filterKey]: { direction, key },
        });
    };

    const favoriteClicked = (symbolId: string) => {
        const body = {
            symbol_ids: symbolId,
            coin_type: FavoriteCoinType.FUTURE,
            exchange_id: futuresSymbol[symbolId].exchangeId,
        };
        if (!favorites.includes(symbolId)) {
            dispatch(createFavorite({ body, isLogin: isAuthenticated }));
        } else {
            dispatch(cancelFavorite({ body, isLogin: isAuthenticated }));
        }
    };

    const handleCloseDrawer = () => {
        clearInput();
        drawerToggler();
    };

    return (
        <DrawerContainer>
            <Drawer handleClose={handleCloseDrawer} anchor="left" open={isDrawerOpen}>
                <DrawerHeader>
                    <span>{t('derivatives')}</span>
                    {isMobile ? (
                        <MoreLess
                            fill={colors.text.primary}
                            stroke={colors.text.primary}
                            style={{ cursor: 'pointer' }}
                            onClick={handleCloseDrawer}
                        />
                    ) : (
                        <Cancel
                            stroke={colors.text.primary}
                            style={{ cursor: 'pointer' }}
                            onClick={handleCloseDrawer}
                        />
                    )}
                </DrawerHeader>
                <DrawerSearchContainer>
                    <Search stroke={colors.text.tertiary} />
                    <input
                        type="text"
                        value={searchText}
                        onChange={handleChange}
                        placeholder="Search"
                    />
                    {searchText ? (
                        <CrossCircle
                            stroke={colors.brand.main}
                            fill={colors.text.tertiary}
                            onClick={clearInput}
                        />
                    ) : (
                        ''
                    )}
                </DrawerSearchContainer>
                <Tabs
                    value={activeTab}
                    onChange={(val) => {
                        clearInput();
                        setActiveTab(val);
                        setActiveFilter({});
                    }}
                    className={'coinTabs'}
                >
                    <Tab
                        key={0}
                        label={t('favorites')}
                        value={0}
                        icon={<Favorites stroke={colors.primary} fill={colors.primary} />}
                    />
                    {isAuthenticated && <Tab key={1} label={t('holdings')} value={1} />}
                    {futuresUnderlying?.map((item, index) => {
                        return <Tab value={index + 2} key={index + 2} label={item.name} />;
                    })}
                </Tabs>
                {activeTab > 1 && (
                    <CategorySelector
                        selectedCategory={categoryFilter}
                        onChange={setCategoryFilter}
                    />
                )}
                {activeTab !== 1 && (
                    <DrawerCoinListHeader>
                        <FlexContainer styles={{ cursor: 'pointer', gap: 4 }}>
                            <FlexContainer
                                onClick={() => setFilter(FILTERS.TRADING_PAIR, 'symbolName')}
                                styles={{ gap: 4 }}
                            >
                                {t('trading_pairs')}
                                <FlexContainer styles={{ flexDirection: 'column' }}>
                                    <SortUp
                                        fill={
                                            activeFilter?.TRADING_PAIR?.direction ===
                                            FILTER_DIRECTION.ASC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                    <SortDown
                                        fill={
                                            activeFilter?.TRADING_PAIR?.direction ===
                                            FILTER_DIRECTION.DESC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                </FlexContainer>
                            </FlexContainer>
                            {'/'}
                            <FlexContainer
                                onClick={() => setFilter(FILTERS.VOLUME, 'qv')}
                                styles={{ gap: 4 }}
                            >
                                {t('vol')}
                                <FlexContainer styles={{ flexDirection: 'column' }}>
                                    <SortUp
                                        fill={
                                            activeFilter?.VOLUME?.direction === FILTER_DIRECTION.ASC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                    <SortDown
                                        fill={
                                            activeFilter?.VOLUME?.direction ===
                                            FILTER_DIRECTION.DESC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                </FlexContainer>
                            </FlexContainer>
                        </FlexContainer>
                        <FlexContainer styles={{ cursor: 'pointer', gap: 4 }}>
                            <FlexContainer
                                onClick={() => setFilter(FILTERS.PRICE, 'c')}
                                styles={{ gap: 4 }}
                            >
                                {t('price')}
                                <FlexContainer styles={{ flexDirection: 'column' }}>
                                    <SortUp
                                        fill={
                                            activeFilter?.PRICE?.direction === FILTER_DIRECTION.ASC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                    <SortDown
                                        fill={
                                            activeFilter?.PRICE?.direction === FILTER_DIRECTION.DESC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                </FlexContainer>
                            </FlexContainer>
                            {'/'}
                            <FlexContainer
                                onClick={() => setFilter(FILTERS.CHANGE, 'm')}
                                styles={{ gap: 4 }}
                            >
                                {`${t('24h_change')} ${!isMobile ? '%' : ''}`}
                                <FlexContainer styles={{ flexDirection: 'column' }}>
                                    <SortUp
                                        fill={
                                            activeFilter?.CHANGE?.direction === FILTER_DIRECTION.ASC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                    <SortDown
                                        fill={
                                            activeFilter?.CHANGE?.direction ===
                                            FILTER_DIRECTION.DESC
                                                ? colors.primary
                                                : colors.text.tertiary
                                        }
                                    />
                                </FlexContainer>
                            </FlexContainer>
                        </FlexContainer>
                    </DrawerCoinListHeader>
                )}
                {activeTab !== 1 && !isEmptyObj(filteredSymbols) && (
                    <DrawerCoinListContainer>
                        {Object.keys(filteredSymbols).map((symbolId: string, index) => {
                            const symbol: TradeSocketFutureData = filteredSymbols[symbolId];

                            return (
                                <DrawerCoinListItem key={index}>
                                    <Favorites
                                        stroke={
                                            favorites.includes(symbolId)
                                                ? colors.primary
                                                : colors.border.dark
                                        }
                                        fill={
                                            favorites.includes(symbolId) ? colors.primary : 'none'
                                        }
                                        onClick={() => favoriteClicked(symbolId)}
                                        className="favoriteButton"
                                    />

                                    <FlexContainer
                                        onClick={() => {
                                            navigate(`${APP_URLS.CONTRACT}/${symbolId}`);
                                            handleCloseDrawer();
                                        }}
                                        className="pairDetails"
                                    >
                                        <FlexContainer styles={{ gap: 8, alignItems: 'center' }}>
                                            {!isMobile && (
                                                <img
                                                    src={
                                                        token[
                                                            symbol.secondLevelUnderlyingId as keyof TokenObj
                                                        ]?.iconUrl || defaultImage
                                                    }
                                                    alt={symbolId}
                                                    width={20}
                                                    height={20}
                                                />
                                            )}

                                            <FlexContainer className="pairInfo">
                                                <FlexContainer styles={{ gap: 4 }}>
                                                    <div className="symbolName">
                                                        {symbol.symbolName}
                                                    </div>
                                                    {symbol.label?.labelValue ===
                                                        TokenLabel.HOT && <Hot />}
                                                    {symbol.label?.labelValue ===
                                                        TokenLabel.NEW && <New />}
                                                </FlexContainer>

                                                <div className="quoteValue">
                                                    {symbol?.qv
                                                        ? `${abbreviateNumber(symbol.qv)} ${
                                                              symbol.quoteTokenName
                                                          }`
                                                        : '--'}
                                                </div>
                                            </FlexContainer>
                                        </FlexContainer>

                                        <FlexContainer className="priceInfo">
                                            <div className="currentPrice">
                                                {symbol.c
                                                    ? currencyFormatter(
                                                          Number(symbol.c),
                                                          DEPTH[symbol?.minPricePrecision],
                                                      )
                                                    : '--'}
                                            </div>

                                            <div
                                                className={`priceChange ${
                                                    Number(symbol?.m) >= 0 ? 'up' : 'down'
                                                }`}
                                            >
                                                {Number(symbol?.m) > 0 && '+'}
                                                {Number(symbol?.m)
                                                    ? `${currencyFormatter(
                                                          Math.floor(Number(symbol.m) * 10000) /
                                                              100,
                                                          2,
                                                      )}%`
                                                    : '0.00%'}
                                            </div>
                                        </FlexContainer>
                                    </FlexContainer>
                                </DrawerCoinListItem>
                            );
                        })}
                    </DrawerCoinListContainer>
                )}
                {activeTab === 1 && (
                    <Holding search={searchText} handleCloseDrawer={handleCloseDrawer} />
                )}
                {((isEmptyObj(filteredSymbols) &&
                    (searchText !== '' || categoryFilter !== '') &&
                    activeTab > 1) ||
                    (searchText !== '' && activeTab === 0)) && (
                    <NoRecordContainer>
                        <NoRecord fill={colors.icon.default} stroke={colors.icon.default} />
                        <p>{t('no_result')}</p>
                    </NoRecordContainer>
                )}
                {activeTab === 0 &&
                    searchText === '' &&
                    !hasCommonValue(Object.keys(futuresSymbol), favorites) && (
                        <NoRecordContainer>
                            <NoFavorites />
                            <p>{t('no_favorites')}</p>
                            <Button onClick={() => setActiveTab(2)}>{t('new_favorites')}</Button>
                        </NoRecordContainer>
                    )}
            </Drawer>
        </DrawerContainer>
    );
};

export { CoinDrawer };
